import { ArrowUpRight, ArrowDownRight, Equal } from 'lucide-react';
import type { GuidanceBridgeBlock, GuidanceItem } from '../lib/types';
import styles from './GuidanceBridge.module.css';

interface Props {
  block: GuidanceBridgeBlock;
  className?: string;
}

const framingLabel: Record<GuidanceItem['framing'], string> = {
  raise: 'Raised',
  reaffirm: 'Reaffirmed',
  lower: 'Lowered',
  introduce: 'Introduced',
};

const framingColor: Record<GuidanceItem['framing'], string> = {
  raise: 'var(--tia-positive)',
  reaffirm: 'var(--tia-info)',
  lower: 'var(--tia-negative)',
  introduce: 'var(--fg-muted)',
};

const verdictColorMap = {
  beat: 'var(--tia-positive)',
  mixed: 'var(--tia-warning)',
  miss: 'var(--tia-negative)',
} as const;

function formatRange(item: GuidanceItem) {
  const lo = item.unit === '%' ? `${item.rangeLo.toFixed(1)}%` : `${item.unit}${item.rangeLo.toFixed(1)}`.replace('$B', '$') + (item.unit === '$B' ? 'B' : '');
  const hi = item.unit === '%' ? `${item.rangeHi.toFixed(1)}%` : `${item.unit}${item.rangeHi.toFixed(1)}`.replace('$B', '$') + (item.unit === '$B' ? 'B' : '');
  return `${lo}–${hi}`;
}

function formatConsensus(item: GuidanceItem) {
  if (item.consensusMid == null) return '—';
  if (item.unit === '%') return `${item.consensusMid.toFixed(1)}%`;
  if (item.unit === '$B') return `$${item.consensusMid.toFixed(1)}B`;
  return `${item.consensusMid.toFixed(2)}${item.unit}`;
}

function formatDelta(item: GuidanceItem) {
  if (item.delta == null) return '—';
  const sign = item.delta > 0 ? '+' : '';
  if (item.unit === '%') return `${sign}${item.delta.toFixed(1)}pp`;
  if (item.unit === '$B') return `${sign}$${item.delta.toFixed(1)}B`;
  return `${sign}${item.delta.toFixed(2)}${item.unit}`;
}

export function GuidanceBridge({ block, className }: Props) {
  return (
    <div className={[styles.wrap, className].filter(Boolean).join(' ')}>
      <header className={styles.header}>
        <span className={styles.eyebrow}>Implied Guidance Bridge</span>
        <p className={styles.summary}>{block.summary}</p>
        <span className={styles.netVerdict} style={{ background: verdictColorMap[block.netVerdict], color: 'var(--tia-white)' }}>
          Net {block.netVerdict}
        </span>
      </header>

      <ul className={styles.list}>
        {block.items.map((item) => {
          const Icon = item.delta == null ? Equal : item.delta > 0 ? ArrowUpRight : item.delta < 0 ? ArrowDownRight : Equal;
          const deltaClass = item.delta == null ? styles.flat : item.delta > 0 ? styles.up : item.delta < 0 ? styles.down : styles.flat;
          return (
            <li key={item.metric} className={styles.row}>
              <div className={styles.metric}>
                <span className={styles.metricName}>{item.metric}</span>
                <span className={styles.framing} style={{ color: framingColor[item.framing] }}>
                  {framingLabel[item.framing]}
                </span>
              </div>
              <div className={styles.range}>
                <span className={styles.rangeLabel}>Guidance</span>
                <span className={[styles.rangeValue, 'mono'].join(' ')}>{formatRange(item)}</span>
              </div>
              <div className={styles.range}>
                <span className={styles.rangeLabel}>Consensus</span>
                <span className={[styles.rangeValue, 'mono'].join(' ')}>{formatConsensus(item)}</span>
                {item.consensusSource && <span className={styles.consensusSource}>{item.consensusSource}</span>}
              </div>
              <div className={[styles.delta, deltaClass].join(' ')}>
                <Icon size={14} strokeWidth={2} aria-hidden="true" />
                <span className="mono">{formatDelta(item)}</span>
              </div>
              {item.note && <p className={styles.note}>{item.note}</p>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
